import { IOGateway } from '@interopio/gateway';

/**
 * Processes visibility rules by converting string matchers to RegExp where appropriate.
 */
export function processVisibilityRules<K extends string>(rules?: IOGateway.VisibilityRule<K>[]) {
    if (rules !== undefined) {
        return rules.map(rule => {
            const result = {...rule} as IOGateway.VisibilityRule<K>;

            // Process all keys in the rule that might be matchers (context, method, domain, etc.)
            // Exclude known non-matcher properties: identity and restrictions
            for (const [key, value] of Object.entries(rule)) {
                if (key !== 'identity' && key !== 'restrictions' && value !== undefined) {
                    // Value should be a Matcher type (string, RegExp, or array)
                    (result as Record<K, IOGateway.Filtering.Matcher>)[key] = IOGateway.Filtering.regexify(value as IOGateway.Filtering.Matcher);
                }
            }

            // Process identity matchers separately
            if (rule.identity !== undefined) {
                result.identity = {};
                for (const [key, value] of Object.entries(rule.identity)) {
                    result.identity[key] = IOGateway.Filtering.regexify(value);
                }
            }

            return result;
        });
    }
}

/**
 * Processes metric filters by converting matchers to RegExp and merging deprecated properties.
 */
export function processMetricFilters(filters?: IOGateway.MetricFilters): IOGateway.MetricFilters | undefined {
    if (!filters) {
        return undefined;
    }

    const result = {...filters};

    if (filters.publishers) {
        result.publishers = filters.publishers.map(publisher => {
            const { metrics: originalMetrics, identity: originalIdentity, ...rest } = publisher;

            // Build result object with proper typing
            type ProcessedPublisher = Omit<typeof publisher, 'metrics' | 'identity'> & {
                identity: Record<string, IOGateway.Filtering.Matcher>;
                metrics: {
                    allow?: IOGateway.Filtering.Matcher[];
                    block?: IOGateway.Filtering.Matcher[];
                };
            };

            const processedPublisher = { ...rest } as ProcessedPublisher;

            // Process identity matchers (always set, even if empty)
            processedPublisher.identity = {};
            if (originalIdentity) {
                for (const [key, value] of Object.entries(originalIdentity)) {
                    processedPublisher.identity[key] = IOGateway.Filtering.regexify(value);
                }
            }

            // Process metrics matchers (allow, block, whitelist, blacklist)
            // Note: whitelist/blacklist are deprecated, merge them with allow/block
            processedPublisher.metrics = {};
            if (originalMetrics) {
                const allow = (originalMetrics.allow ?? originalMetrics.whitelist ?? []).map(m => IOGateway.Filtering.regexify(m));
                const block = (originalMetrics.block ?? originalMetrics.blacklist ?? []).map(m => IOGateway.Filtering.regexify(m));

                // Only include non-empty arrays to avoid meaningless empty filters
                if (allow.length > 0) {
                    processedPublisher.metrics.allow = allow;
                }
                if (block.length > 0) {
                    processedPublisher.metrics.block = block;
                }
            }

            return processedPublisher as typeof publisher;
        });
    }

    return result;
}

/**
 * Processes publisher configuration by processing its filters.
 */
export function processPublisherConfig<T extends IOGateway.BasicMetricsPublisherConfig>(publisherConfig?: T): T | undefined {
    if (!publisherConfig) {
        return undefined;
    }

    const result = {...publisherConfig};

    if (publisherConfig.filters) {
        result.filters = processMetricFilters(publisherConfig.filters);
    }

    return result;
}

/**
 * Processes mesh configuration, returning undefined if disabled.
 */
export function processMeshConfig(meshConfig?: IOGateway.MeshConfig & { enabled?: boolean }): IOGateway.MeshConfig | undefined {
    if (meshConfig?.enabled === false) {
        return undefined;
    }
    return meshConfig;
}

/**
 * Processes metrics configuration by handling filters and publisher configs.
 */
export function processMetricsConfig(config?: IOGateway.MetricsConfig & { enabled?: boolean }): IOGateway.MetricsConfig | undefined {
    if (config === undefined || config?.enabled === false) {
        return undefined;
    }
    const result = { ...(config) };

    // Process top-level filters
    if (config.filters) {
        result.filters = processMetricFilters(config.filters);
    }

    // Process file publisher config
    if (config.file) {
        result.file = processPublisherConfig(config.file);
    }

    // Process rest publisher config
    if (config.rest) {
        result.rest = processPublisherConfig(config.rest);
    }

    // Process publishers array (which can contain custom publishers with filters)
    if (config.publishers) {
        result.publishers = config.publishers.map(publisher => {
            if (typeof publisher === 'string') {
                return [publisher];
            }
            // It's a CustomMetricsPublisherConfig
            const c = processPublisherConfig(publisher);
            if (c !== undefined) {
                return [c];
            }
            return [];
        }).flat();
    }

    return result;
}

/**
 * Processes gateway configuration by converting string matchers to RegExp and handling special flags.
 * This ensures that configurations loaded from JSON files work correctly.
 */
export function processGatewayConfig(config: IOGateway.GatewayConfig & {
    metrics?: IOGateway.MetricsConfig & { enabled?: boolean },
    mesh?: IOGateway.MeshConfig & { enabled?: boolean }
}): IOGateway.GatewayConfig {
    // regexify in case config is coming from JSON config file
    const result = {...config};

    if (config.contexts) {
        result.contexts = {
            ...config.contexts,
            visibility: processVisibilityRules(config.contexts.visibility)
        };
    }

    if (config.methods) {
        result.methods = {
            ...config.methods,
            visibility: processVisibilityRules(config.methods.visibility)
        };
    }

    if (config.peers) {
        result.peers = {
            ...config.peers,
            visibility: processVisibilityRules(config.peers.visibility)
        };
    }

    if (config.metrics) {
        result.metrics = processMetricsConfig(config.metrics);
    }

    if (config.mesh) {
        result.mesh = processMeshConfig(config.mesh);
    }

    return result;
}
